import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../api/axios';
import { toast } from 'sonner';

// Cookie helper
const setCookie = (name, value, days = 7) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
};

const AdminLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [magicLoading, setMagicLoading] = useState(false);
  const [magicNote, setMagicNote] = useState('');
  const [error, setError] = useState('');

  // Set admin manifest for PWA installation
  useEffect(() => {
    // Update manifest link
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) {
      manifestLink.href = '/admin-manifest.json';
    }
    
    // Update apple-touch-icon for iOS
    const appleIcons = document.querySelectorAll('link[rel="apple-touch-icon"]');
    appleIcons.forEach(icon => {
      icon.href = '/admin-icon-192.png';
    });
    
    // Update apple-mobile-web-app-title for iOS
    let titleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (titleMeta) {
      titleMeta.content = 'HYS Admin';
    }
    
    // Update theme color
    let themeMeta = document.querySelector('meta[name="theme-color"]');
    if (themeMeta) {
      themeMeta.content = '#5D4037';
    }
    
    // Update page title
    document.title = 'Admin Anmeldung - Homeyoursoul';
  }, []);

  // Check if already logged in
  useEffect(() => {
    const checkAuth = () => {
      try {
        const stored = localStorage.getItem('auth-storage');
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed?.state?.user?.role === 'admin' && parsed?.state?.token) {
            console.log('[ADMIN-LOGIN] Already logged in, redirecting...');
            window.location.href = '/admin';
            return;
          }
        }
        
        // Also check cookies
        const cookieToken = document.cookie.split('; ').find(row => row.startsWith('auth-token='));
        const cookieUser = document.cookie.split('; ').find(row => row.startsWith('auth-user='));
        if (cookieToken && cookieUser) {
          const user = JSON.parse(decodeURIComponent(cookieUser.split('=')[1]));
          if (user?.role === 'admin') {
            console.log('[ADMIN-LOGIN] Found admin cookie, redirecting...');
            window.location.href = '/admin';
          }
        }
      } catch (e) {
        console.error('[ADMIN-LOGIN] Auth check error:', e);
      }
    };
    
    checkAuth();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      console.log('[ADMIN-LOGIN] Attempting login...');
      const response = await axiosInstance.post('/auth/login', { email, password });
      const { user, token } = response.data;

      if (user?.role !== 'admin') {
        setError('Kein Admin-Zugang. Nur Administratoren können sich hier anmelden.');
        setLoading(false);
        return;
      }

      console.log('[ADMIN-LOGIN] Login successful, saving auth...');
      const now = Date.now();

      // Save to localStorage
      try {
        const authState = {
          version: 1,
          state: { user, token, isAuthenticated: true, lastActivity: now }
        };
        localStorage.setItem('auth-storage', JSON.stringify(authState));
        localStorage.setItem('token', token);
        localStorage.setItem('last-activity', now.toString());
        console.log('[ADMIN-LOGIN] Saved to localStorage');
      } catch (storageError) {
        console.error('[ADMIN-LOGIN] localStorage error:', storageError);
      }

      // Save to cookies (Safari PWA fallback)
      try {
        setCookie('auth-token', token);
        setCookie('auth-user', JSON.stringify(user));
        setCookie('auth-activity', now.toString());
        console.log('[ADMIN-LOGIN] Saved to cookies');
      } catch (cookieError) {
        console.error('[ADMIN-LOGIN] Cookie error:', cookieError);
      }

      toast.success('Erfolgreich angemeldet!');

      // Wait a bit to ensure storage is persisted
      await new Promise(resolve => setTimeout(resolve, 300));

      // Redirect to admin
      console.log('[ADMIN-LOGIN] Redirecting to /admin...');
      window.location.href = '/admin';

    } catch (err) {
      console.error('[ADMIN-LOGIN] Login error:', err);
      
      // Provide more helpful error messages
      let errorMessage = 'Anmeldung fehlgeschlagen.';
      
      if (err.response?.status === 401) {
        errorMessage = 'E-Mail oder Passwort ist falsch. Bitte überprüfen Sie Ihre Eingaben.';
      } else if (err.response?.status === 403) {
        errorMessage = 'Zugang verweigert. Sie haben keine Admin-Berechtigung.';
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (!navigator.onLine) {
        errorMessage = 'Keine Internetverbindung. Bitte prüfen Sie Ihre Verbindung.';
      } else {
        errorMessage = 'Verbindungsfehler. Bitte versuchen Sie es erneut oder laden Sie die Seite neu.';
      }
      
      setError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #C4A084 0%, #B89968 100%)' }}>
      <div className="bg-white p-8 w-full max-w-md shadow-2xl">
        <div className="text-center mb-8">
          <div className="inline-block px-4 py-1 mb-3 text-sm font-medium text-white" style={{ backgroundColor: '#C4A084' }}>
            NUR FÜR ADMINS
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Admin Anmeldung</h1>
          <p className="text-gray-600">Homeyoursoul Verwaltung</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
              <p>{error}</p>
              <button 
                type="button"
                onClick={() => {
                  // Clear all caches
                  localStorage.clear();
                  sessionStorage.clear();
                  document.cookie.split(";").forEach(c => {
                    document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
                  });
                  window.location.reload();
                }}
                className="mt-2 text-xs underline hover:no-underline"
              >
                Cache löschen und neu laden
              </button>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              E-Mail
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="w-full px-4 py-3 border border-gray-300 focus:ring-2 focus:ring-stone-600 focus:border-stone-600 outline-none"
              placeholder="admin@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Passwort
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="w-full px-4 py-3 border border-gray-300 focus:ring-2 focus:ring-stone-600 focus:border-stone-600 outline-none"
              placeholder="••••••••"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 text-white font-semibold disabled:opacity-50 transition-all min-h-[48px]"
            style={{ backgroundColor: '#C4A084' }}
          >
            {loading ? 'Anmelden...' : 'Anmelden'}
          </button>
        </form>

        <div className="mt-6 text-center space-y-3">
          <button
            type="button"
            onClick={async () => {
              if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                setError('Bitte oben eine gültige E-Mail-Adresse eingeben.');
                return;
              }
              setError('');
              setMagicLoading(true);
              try {
                await axiosInstance.post('/auth/magic-link/request', { email });
                setMagicNote('Wenn diese E-Mail registriert ist, haben wir einen Anmelde-Link gesendet. Bitte prüfen Sie Ihr Postfach.');
                toast.success('Magic-Link gesendet (sofern E-Mail bekannt)');
              } catch (e) {
                setError(e.response?.data?.detail || 'Anfrage fehlgeschlagen');
              } finally {
                setMagicLoading(false);
              }
            }}
            disabled={magicLoading}
            className="w-full py-3 text-sm border min-h-[44px] flex items-center justify-center gap-2"
            style={{ background: 'transparent', borderColor: '#C4A084', color: '#5D4E3C' }}
            data-testid="admin-magic-link-btn"
          >
            {magicLoading ? (
              <span>Wird gesendet…</span>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.7} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4" />
                </svg>
                <span>Magic-Link per E-Mail anfordern</span>
              </>
            )}
          </button>
          {magicNote && (
            <div className="text-xs p-3 text-left" style={{ background: '#FAF5EE', border: '1px solid #E8DCC8', color: '#5D4E3C' }} data-testid="admin-magic-note">
              {magicNote}
            </div>
          )}
          <a href="/forgot-password" className="block text-amber-700 hover:text-amber-800 text-sm underline" data-testid="admin-forgot-password-link">
            Passwort vergessen?
          </a>
          <a href="/" className="block text-amber-700 hover:text-amber-800 text-sm">
            ← Zurück zum Shop
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
